import type { Action, Dictionary } from '@protector/utils/types';
import moment from 'moment';
import type { ActionsObservable, StateObservable } from 'redux-observable';
import { ofType } from 'redux-observable';
import type { AppState } from 'redux/app-state';
import { concat, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, retryWhen, take, withLatestFrom } from 'rxjs/operators';
import {
  LoadWeatherFailure,
  LoadWeatherHourlyFailure,
  LoadWeatherHourlySuccess,
  LoadWeatherSuccess,
  WeatherActionsTypes
} from './weather.actions';
import { hasWeatherDataCacheByTime, orderWeatherData } from './weather.functions';
import type { LoadWeatherParams, WeatherData, WeatherResponseError } from './weather.models';
import { getWeather, getWeatherHours } from './weather.service';

export const handleLoadWeather = (action$: ActionsObservable<Action<LoadWeatherParams>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(WeatherActionsTypes.LOAD_WEATHER),
    map((action: Action<LoadWeatherParams>) => action.payload),
    filter((payload): payload is LoadWeatherParams => payload !== undefined),
    withLatestFrom(
      state$.pipe(map((state: AppState) => state.analytics.weather.data)),
      state$.pipe(map((state: AppState) => state.uiState.global.systemFlags))
    ),
    mergeMap(([request, weatherData, systemFlags]: [LoadWeatherParams, WeatherData[], Dictionary<string | boolean> | null]) => {
      const flagRemoveReverseCoordinates = !!systemFlags?.P40_49783_REMOVE_REVERSE_LONG_LAT;

      const hasWeatherCache = hasWeatherDataCacheByTime({ weatherData, request });
      if (hasWeatherCache) {
        return of();
      }

      return getWeather(request, flagRemoveReverseCoordinates).pipe(
        retryWhen(errors => errors.pipe(delay(1000), take(3))),
        map(response => response.data),
        mergeMap(weather => {
          if (typeof weather === 'object' && (weather as unknown as WeatherResponseError).statusCode) {
            const message = (weather as unknown as WeatherResponseError).message;
            return concat([LoadWeatherFailure(message)]);
          }
          const resultWeather: WeatherData = {
            content: orderWeatherData(weather),
            propertyName: request.propertyName,
            propertyId: request.propertyId,
            companyId: request.companyId,
            dataSource: request.dataSource,
            dateTimeUpdate: moment().toISOString()
          };
          return concat([LoadWeatherSuccess(resultWeather)]);
        }),
        catchError((error: string) => of(LoadWeatherFailure(error)))
      );
    }, 2)
  );

export const handleLoadWeatherHourly = (action$: ActionsObservable<Action<LoadWeatherParams>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(WeatherActionsTypes.LOAD_WEATHER_HOURLY),
    map((action: Action<LoadWeatherParams>) => action.payload),
    filter((payload): payload is LoadWeatherParams => payload !== undefined),
    withLatestFrom(
      state$.pipe(map((state: AppState) => state.analytics.weather.dataHourly)),
      state$.pipe(map((state: AppState) => state.uiState.global.systemFlags))
    ),
    mergeMap(([request, weatherData, systemFlags]: [LoadWeatherParams, WeatherData[], Dictionary<string | boolean> | null]) => {
      const flagRemoveReverseCoordinates = !!systemFlags?.P40_49783_REMOVE_REVERSE_LONG_LAT;

      const hasWeatherCache = hasWeatherDataCacheByTime({ weatherData, request });
      if (hasWeatherCache) {
        return of();
      }

      return getWeatherHours(request, flagRemoveReverseCoordinates).pipe(
        retryWhen(errors => errors.pipe(delay(1000), take(3))),
        map(response => response.data),
        mergeMap(weather => {
          const resultWeather: WeatherData = {
            content: orderWeatherData(weather),
            propertyName: request.propertyName,
            propertyId: request.propertyId,
            companyId: request.companyId,
            dataSource: request.dataSource,
            dateTimeUpdate: moment().toISOString()
          };
          return concat([LoadWeatherHourlySuccess(resultWeather)]);
        }),
        catchError((error: string) => of(LoadWeatherHourlyFailure(error)))
      );
    }, 2)
  );
